import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronDown,
  faChevronUp,
  faDollarSign,
  faCalendar,
  faBullseye,
  faUsers,
  faChartLine,
  faLightbulb,
  faClock,
  faCompass,
  faGlobe,
  faTag,
  faAd,
  faRocket,
  faSpinner
} from '@fortawesome/free-solid-svg-icons';
import { jsPDF } from 'jspdf';
import { api, getApiUrl } from '../utils/apiConfig';
import { useAccount } from '../contexts/AccountContext';
import { getUserData } from '../utils/userUtils';
import CreateCampaignModal from './CreateCampaignModal';
import './MediaPlanDisplay.css';

/**
 * MediaPlanDisplay - Displays generated media plan with collapsible sections
 *
 * Props:
 * - plan: The generated media plan object
 * - onEdit: Callback when user wants to edit/revise the plan
 * - mediaPlanName: Name of the media plan (for campaign naming)
 */
const MediaPlanDisplay = ({ plan, onEdit, mediaPlanName }) => {
  const { getAccountIdToUse } = useAccount();
  const [user] = useState(() => getUserData());
  const accountId = getAccountIdToUse();

  const [expandedSections, setExpandedSections] = useState({
    overview: true,
    channels: true,
    strategy: true,
    timeline: true,
    recommendations: true
  });

  // Channel/Platform display name - normalize for display
  const getChannelName = (channel) => {
    const names = {
      'dv360': 'DV360',
      'ttd': 'The Trade Desk',
      'facebook': 'Facebook',
      'meta': 'Meta',
      'google_ads': 'Google Ads Search',
      'google-ads-search': 'Google Ads Search',
      'google_ads_search': 'Google Ads Search',
      'tiktok': 'TikTok',
      'reddit': 'Reddit',
      'linkedin': 'LinkedIn',
      'madhive': 'MadHive'
    };
    return names[channel?.toLowerCase()] || channel || 'N/A';
  };

  // Track expanded line items per campaign
  const [expandedLineItems, setExpandedLineItems] = useState({});

  // Track which channel is being processed for campaign creation
  const [creatingCampaignFor, setCreatingCampaignFor] = useState(null);

  // Modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [insertionOrderFile, setInsertionOrderFile] = useState(null);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const toggleLineItem = (key) => {
    setExpandedLineItems(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  if (!plan) return null;

  const { overview, channels, strategy, timeline } = plan;

  // Get optimization recommendations from strategy
  const optimizationRecommendations = strategy?.optimization_recommendations || [];

  /**
   * Get items array from campaign (handles both old line_items and new ad_sets/ad_groups formats)
   */
  const getCampaignItems = (campaign) => {
    if (campaign.line_items) return { items: campaign.line_items, type: 'line_items' };
    if (campaign.ad_sets) return { items: campaign.ad_sets, type: 'ad_sets' };
    if (campaign.ad_groups) return { items: campaign.ad_groups, type: 'ad_groups' };
    return { items: [], type: 'unknown' };
  };

  /**
   * Normalize targeting from new Facebook ad_set format to display format
   */
  const normalizeAdSetTargeting = (adSet) => {
    const targeting = {};

    // From targeting_profiles
    if (adSet.targeting_profiles?.length > 0) {
      const profile = adSet.targeting_profiles[0]; // Use first profile for display

      if (profile.age_min || profile.age_max) {
        targeting.age = [`${profile.age_min || 18}-${profile.age_max || 65}+`];
      }
      if (profile.genders?.length > 0) {
        targeting.gender = profile.genders.map(g => g === 1 ? 'Male' : g === 2 ? 'Female' : 'All');
      }
      if (profile.interests?.length > 0) {
        targeting.interests = profile.interests;
      }
      if (profile.geo_locations?.countries?.length > 0) {
        targeting.geo = profile.geo_locations.countries;
      }
    }

    // From placements
    if (adSet.placements?.length > 0) {
      targeting.placements = adSet.placements.map(p => p.replace(/_/g, ' '));
    }

    return targeting;
  };

  /**
   * Normalize Google Ads ad_group to display format
   */
  const normalizeAdGroupTargeting = (adGroup) => {
    const targeting = {};

    if (adGroup.keywords?.length > 0) {
      targeting.keywords = adGroup.keywords.map(k =>
        typeof k === 'string' ? k : `${k.text} (${k.match_type?.toLowerCase() || 'broad'})`
      );
    }

    return targeting;
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Generate insertion order as PDF
  const generateInsertionOrderPDF = (channel) => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 20;
    const contentWidth = pageWidth - margin * 2;
    let y = 20;

    // Helper to add text with auto page break
    const addText = (text, fontSize = 10, isBold = false, indent = 0) => {
      doc.setFontSize(fontSize);
      doc.setFont('helvetica', isBold ? 'bold' : 'normal');
      const lines = doc.splitTextToSize(text, contentWidth - indent);
      lines.forEach(line => {
        if (y > 270) { doc.addPage(); y = 20; }
        doc.text(line, margin + indent, y);
        y += fontSize * 0.5;
      });
    };

    // Header
    doc.setFillColor(30, 58, 138);
    doc.rect(0, 0, pageWidth, 35, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text('INSERTION ORDER', pageWidth / 2, 22, { align: 'center' });
    doc.setTextColor(0, 0, 0);
    y = 45;

    // Plan Info
    addText(`Media Plan: ${mediaPlanName || 'N/A'}`, 11, true);
    y += 2;
    if (overview?.campaign_name) { addText(`Campaign: ${overview.campaign_name}`, 10); y += 2; }
    addText(`Generated: ${new Date().toLocaleDateString()}`, 10);
    y += 8;

    // Platform Details Section
    doc.setFillColor(241, 245, 249);
    doc.rect(margin, y, contentWidth, 8, 'F');
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('PLATFORM DETAILS', margin + 4, y + 6);
    y += 14;

    addText(`Platform: ${getChannelName(channel.platform)}`, 10);
    addText(`Total Budget: ${formatCurrency(channel.budget)}`, 10);
    if (overview?.start_date && overview?.end_date) {
      addText(`Flight Dates: ${overview.start_date} - ${overview.end_date}`, 10);
    }
    y += 8;

    // Campaigns Section
    if (channel.campaigns?.length > 0) {
      doc.setFillColor(241, 245, 249);
      doc.rect(margin, y, contentWidth, 8, 'F');
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('CAMPAIGNS', margin + 4, y + 6);
      y += 14;

      channel.campaigns.forEach((campaign, idx) => {
        if (y > 250) { doc.addPage(); y = 20; }
        addText(`Campaign ${idx + 1}: ${campaign.campaign_name}`, 11, true);
        addText(`Budget: ${formatCurrency(campaign.budget)}`, 10, false, 8);
        addText(`Objective: ${campaign.objective || campaign.campaign_type || 'N/A'}`, 10, false, 8);
        y += 4;

        // Handle old format (line_items)
        if (campaign.line_items?.length > 0) {
          addText('Line Items / Ad Groups:', 10, true, 8);
          campaign.line_items.forEach((li, liIdx) => {
            if (y > 250) { doc.addPage(); y = 20; }
            addText(`${liIdx + 1}. ${li.name}`, 10, false, 12);
            addText(`Type: ${li.type} | Budget: ${formatCurrency(li.budget)} | Bid: $${li.bid_amount}`, 9, false, 16);

            if (li.targeting) {
              const targeting = [];
              if (li.targeting.geo?.length) targeting.push(`Geo: ${li.targeting.geo.join(', ')}`);
              if (li.targeting.age?.length) targeting.push(`Age: ${li.targeting.age.join(', ')}`);
              if (li.targeting.gender?.length) targeting.push(`Gender: ${li.targeting.gender.join(', ')}`);
              if (li.targeting.devices?.length) targeting.push(`Devices: ${li.targeting.devices.join(', ')}`);
              if (li.targeting.placements?.length) targeting.push(`Placements: ${li.targeting.placements.join(', ')}`);
              if (li.targeting.interests?.length) targeting.push(`Interests: ${li.targeting.interests.join(', ')}`);
              if (li.targeting.keywords?.length) targeting.push(`Keywords: ${li.targeting.keywords.join(', ')}`);
              if (targeting.length) addText(`Targeting: ${targeting.join(' | ')}`, 9, false, 16);
            }
            if (li.creatives?.ad_formats?.length) {
              addText(`Ad Formats: ${li.creatives.ad_formats.join(', ')}`, 9, false, 16);
            }
            if (li.creatives?.call_to_action) {
              addText(`CTA: ${li.creatives.call_to_action}`, 9, false, 16);
            }
            y += 3;
          });
        }

        // Handle new Facebook format (ad_sets)
        if (campaign.ad_sets?.length > 0) {
          addText('Ad Sets:', 10, true, 8);
          campaign.ad_sets.forEach((adSet, asIdx) => {
            if (y > 250) { doc.addPage(); y = 20; }
            addText(`${asIdx + 1}. ${adSet.name}`, 10, false, 12);
            const budgetStr = adSet.budget ? formatCurrency(adSet.budget) :
                             adSet.daily_budget ? `${formatCurrency(adSet.daily_budget)}/day` : 'N/A';
            addText(`Tactic: ${adSet.tactic || 'N/A'} | Budget: ${budgetStr}`, 9, false, 16);

            // Targeting from targeting_profiles
            if (adSet.targeting_profiles?.length > 0) {
              const profile = adSet.targeting_profiles[0];
              const targeting = [];
              if (profile.geo_locations?.countries?.length) targeting.push(`Geo: ${profile.geo_locations.countries.join(', ')}`);
              if (profile.age_min || profile.age_max) targeting.push(`Age: ${profile.age_min || 18}-${profile.age_max || 65}+`);
              if (profile.genders?.length) {
                const genderNames = profile.genders.map(g => g === 1 ? 'Male' : g === 2 ? 'Female' : 'All');
                targeting.push(`Gender: ${genderNames.join(', ')}`);
              }
              if (profile.interests?.length) targeting.push(`Interests: ${profile.interests.slice(0, 5).join(', ')}${profile.interests.length > 5 ? '...' : ''}`);
              if (targeting.length) addText(`Targeting: ${targeting.join(' | ')}`, 9, false, 16);
            }
            if (adSet.placements?.length) {
              addText(`Placements: ${adSet.placements.map(p => p.replace(/_/g, ' ')).join(', ')}`, 9, false, 16);
            }
            if (adSet.creatives?.ad_formats?.length) {
              addText(`Ad Formats: ${adSet.creatives.ad_formats.join(', ')}`, 9, false, 16);
            }
            if (adSet.creatives?.call_to_action) {
              addText(`CTA: ${adSet.creatives.call_to_action}`, 9, false, 16);
            }
            y += 3;
          });
        }

        // Handle new Google Ads format (ad_groups)
        if (campaign.ad_groups?.length > 0) {
          addText('Ad Groups:', 10, true, 8);
          campaign.ad_groups.forEach((adGroup, agIdx) => {
            if (y > 250) { doc.addPage(); y = 20; }
            addText(`${agIdx + 1}. ${adGroup.name}`, 10, false, 12);
            const bidStr = adGroup.max_cpc_bid_micros ? `Max CPC: ${formatCurrency(adGroup.max_cpc_bid_micros / 1000000)}` : 'N/A';
            addText(`${bidStr}`, 9, false, 16);

            if (adGroup.keywords?.length > 0) {
              const kwText = adGroup.keywords.map(k =>
                typeof k === 'string' ? k : `${k.text} (${k.match_type?.toLowerCase() || 'broad'})`
              ).slice(0, 8).join(', ');
              addText(`Keywords: ${kwText}${adGroup.keywords.length > 8 ? '...' : ''}`, 9, false, 16);
            }
            if (adGroup.ads?.length > 0) {
              addText(`Ads: ${adGroup.ads.length} responsive search ad(s)`, 9, false, 16);
              adGroup.ads.forEach((ad, adIdx) => {
                if (ad.final_url) addText(`URL ${adIdx + 1}: ${ad.final_url}`, 8, false, 20);
              });
            }
            y += 3;
          });
        }
        y += 6;
      });
    }

    // Footer
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(128, 128, 128);
      doc.text(`Page ${i} of ${pageCount}`, pageWidth / 2, 290, { align: 'center' });
    }

    return doc.output('blob');
  };

  // Handle Create Campaign button click - open modal
  const handleCreateCampaign = async (channel, channelIdx) => {
    setCreatingCampaignFor(channelIdx);

    try {
      // Generate insertion order PDF
      const pdfBlob = generateInsertionOrderPDF(channel);
      const fileName = `Insertion_Order_${channel.platform.replace(/\s+/g, '_')}_${Date.now()}.pdf`;
      const file = new File([pdfBlob], fileName, { type: 'application/pdf' });

      // Upload file to GCS
      const formData = new FormData();
      formData.append('file', file);
      formData.append('accountId', accountId);
      formData.append('userId', user?.userId || user?.id);

      const uploadResponse = await api.post(getApiUrl('/api/upload'), formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const fileData = uploadResponse.data?.data;

      // Set state and open modal
      setSelectedChannel(channel);
      setInsertionOrderFile({
        file,
        fileData: fileData ? {
          originalName: fileData.originalName,
          gcsPath: fileData.gcsPath,
          publicUrl: fileData.publicUrl,
          expiresAt: fileData.expiresAt
        } : null
      });
      setShowCreateModal(true);
    } catch (err) {
      console.error('Error preparing campaign:', err);
      alert('Failed to prepare campaign creation. Please try again.');
    } finally {
      setCreatingCampaignFor(null);
    }
  };

  // Close modal and reset state
  const handleCloseModal = () => {
    setShowCreateModal(false);
    setSelectedChannel(null);
    setInsertionOrderFile(null);
  };

  return (
    <div className="media-plan-display">
      {/* Overview Section */}
      <div className="plan-section">
        <button 
          className="section-header" 
          onClick={() => toggleSection('overview')}
        >
          <div className="section-title">
            <FontAwesomeIcon icon={faBullseye} className="section-icon" />
            <h3>Campaign Overview</h3>
          </div>
          <FontAwesomeIcon 
            icon={expandedSections.overview ? faChevronUp : faChevronDown} 
          />
        </button>
        
        {expandedSections.overview && overview && (
          <div className="section-content">
            <div className="overview-header">
              <h2 className="campaign-name">{overview.campaign_name}</h2>
              <div className="overview-meta">
                <span className="meta-item">
                  <FontAwesomeIcon icon={faCalendar} />
                  {overview.start_date} - {overview.end_date}
                </span>
                <span className="meta-item">
                  <FontAwesomeIcon icon={faDollarSign} />
                  {formatCurrency(overview.total_budget)}
                </span>
              </div>
            </div>

            {/* Budget Allocation */}
            <div className="budget-allocation">
              <h4>Budget Allocation</h4>
              <div className="allocation-bars">
                {Object.entries(overview.budget_allocation || {}).map(([channel, data]) => (
                  <div key={channel} className="allocation-item">
                    <div className="allocation-label">
                      <span className="channel-name">{getChannelName(channel)}</span>
                      <span className="channel-amount">
                        {formatCurrency(data.amount)} ({data.percentage}%)
                      </span>
                    </div>
                    <div className="allocation-bar">
                      <div 
                        className="allocation-fill" 
                        style={{ width: `${data.percentage}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Objectives */}
            {overview.objectives && (
              <div className="objectives-section">
                <h4>
                  <FontAwesomeIcon icon={faBullseye} />
                  Objectives
                </h4>
                <ul className="objectives-list">
                  {overview.objectives.map((obj, idx) => (
                    <li key={idx}>{obj}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Target Audience */}
            {overview.target_audience && (
              <div className="audience-section">
                <h4>
                  <FontAwesomeIcon icon={faUsers} />
                  Target Audience
                </h4>
                <p>{overview.target_audience}</p>
              </div>
            )}

            {/* KPIs */}
            {overview.kpis && (
              <div className="kpis-section">
                <h4>
                  <FontAwesomeIcon icon={faChartLine} />
                  Key Performance Indicators
                </h4>
                <div className="kpi-tags">
                  {overview.kpis.map((kpi, idx) => (
                    <span key={idx} className="kpi-tag">{kpi}</span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Channels Section */}
      <div className="plan-section">
        <button
          className="section-header"
          onClick={() => toggleSection('channels')}
        >
          <div className="section-title">
            <FontAwesomeIcon icon={faChartLine} className="section-icon" />
            <h3>Channel Details</h3>
            <span className="section-badge">{channels?.length || 0} channels</span>
          </div>
          <FontAwesomeIcon
            icon={expandedSections.channels ? faChevronUp : faChevronDown}
          />
        </button>

        {expandedSections.channels && channels && (
          <div className="section-content channels-content">
            {channels.map((channel, channelIdx) => (
              <div key={channelIdx} className="channel-card">
                <div className="channel-header">
                  <div className="channel-header-left">
                    <h4 className="platform-name">{getChannelName(channel.platform)}</h4>
                    <span className="channel-budget">{formatCurrency(channel.budget)}</span>
                  </div>
                  <button
                    className="create-campaign-btn"
                    onClick={() => handleCreateCampaign(channel, channelIdx)}
                    disabled={creatingCampaignFor !== null}
                  >
                    {creatingCampaignFor === channelIdx ? (
                      <>
                        <FontAwesomeIcon icon={faSpinner} spin />
                        Preparing...
                      </>
                    ) : (
                      <>
                        <FontAwesomeIcon icon={faRocket} />
                        Create Campaign
                      </>
                    )}
                  </button>
                </div>

                {channel.campaigns?.map((campaign, campIdx) => {
                  const { items, type: itemsType } = getCampaignItems(campaign);

                  return (
                  <div key={campIdx} className="campaign-card">
                    <div className="campaign-header">
                      <h5>{campaign.campaign_name}</h5>
                      <div className="campaign-meta">
                        <span className="campaign-budget">{formatCurrency(campaign.budget)}</span>
                        <span className="campaign-objective">{campaign.objective}</span>
                        {campaign.campaign_type && (
                          <span className="campaign-type">{campaign.campaign_type}</span>
                        )}
                      </div>
                    </div>

                    {/* Render based on item type */}
                    {items.map((item, itemIdx) => {
                      const itemKey = `${channelIdx}-${campIdx}-${itemIdx}`;
                      const isExpanded = expandedLineItems[itemKey];

                      // Normalize targeting based on item type
                      const targeting = itemsType === 'ad_sets'
                        ? normalizeAdSetTargeting(item)
                        : itemsType === 'ad_groups'
                        ? normalizeAdGroupTargeting(item)
                        : item.targeting;

                      return (
                        <div key={itemIdx} className={`line-item ${isExpanded ? 'expanded' : ''}`}>
                          <div
                            className="line-item-header clickable"
                            onClick={() => toggleLineItem(itemKey)}
                          >
                            <span className="line-item-name">{item.name}</span>
                            <span className="line-item-type">
                              {itemsType === 'ad_sets' ? (item.tactic || 'Ad Set')
                                : itemsType === 'ad_groups' ? 'Ad Group'
                                : item.type}
                            </span>
                            <span className="line-item-budget">
                              {item.budget ? formatCurrency(item.budget)
                                : item.daily_budget ? `${formatCurrency(item.daily_budget)}/day`
                                : item.max_cpc_bid_micros ? `Max CPC: ${formatCurrency(item.max_cpc_bid_micros / 1000000)}`
                                : '-'}
                            </span>
                            {item.bid_amount && (
                              <span className="line-item-bid">Bid: ${item.bid_amount}</span>
                            )}
                            <FontAwesomeIcon
                              icon={isExpanded ? faChevronUp : faChevronDown}
                              className="line-item-toggle"
                            />
                          </div>

                          {/* Expanded Details */}
                          {isExpanded && (
                            <div className="line-item-details">
                              {/* Full Targeting Details */}
                              {targeting && Object.keys(targeting).length > 0 && (
                                <div className="targeting-full">
                                  <h6><FontAwesomeIcon icon={faGlobe} /> Targeting</h6>

                                  {targeting.geo?.length > 0 && (
                                    <div className="targeting-row">
                                      <span className="targeting-label">Geography:</span>
                                      <span className="targeting-value">{targeting.geo.join(', ')}</span>
                                    </div>
                                  )}

                                  {targeting.age?.length > 0 && (
                                    <div className="targeting-row">
                                      <span className="targeting-label">Age Groups:</span>
                                      <span className="targeting-value">{targeting.age.join(', ')}</span>
                                    </div>
                                  )}

                                  {targeting.gender?.length > 0 && (
                                    <div className="targeting-row">
                                      <span className="targeting-label">Gender:</span>
                                      <span className="targeting-value">{targeting.gender.join(', ')}</span>
                                    </div>
                                  )}

                                  {targeting.devices?.length > 0 && (
                                    <div className="targeting-row">
                                      <span className="targeting-label">Devices:</span>
                                      <span className="targeting-value">{targeting.devices.join(', ')}</span>
                                    </div>
                                  )}

                                  {targeting.placements?.length > 0 && (
                                    <div className="targeting-row">
                                      <span className="targeting-label">Placements:</span>
                                      <span className="targeting-value">{targeting.placements.join(', ')}</span>
                                    </div>
                                  )}

                                  {targeting.interests?.length > 0 && (
                                    <div className="targeting-row">
                                      <span className="targeting-label">Interests:</span>
                                      <div className="targeting-tags">
                                        {targeting.interests.map((interest, i) => (
                                          <span key={i} className="targeting-tag">{interest}</span>
                                        ))}
                                      </div>
                                    </div>
                                  )}

                                  {targeting.keywords?.length > 0 && (
                                    <div className="targeting-row keywords-row">
                                      <span className="targeting-label"><FontAwesomeIcon icon={faTag} /> Keywords:</span>
                                      <div className="keywords-list">
                                        {targeting.keywords.map((keyword, i) => (
                                          <span key={i} className="keyword-tag">
                                            {typeof keyword === 'string' ? keyword : keyword}
                                          </span>
                                        ))}
                                      </div>
                                    </div>
                                  )}

                                  {targeting.custom_audiences?.length > 0 && (
                                    <div className="targeting-row">
                                      <span className="targeting-label">Custom Audiences:</span>
                                      <span className="targeting-value">{targeting.custom_audiences.join(', ')}</span>
                                    </div>
                                  )}
                                </div>
                              )}

                              {/* Ad Set specific details (Facebook new format) */}
                              {itemsType === 'ad_sets' && (
                                <>
                                  {item.frequency_control && (
                                    <div className="targeting-full">
                                      <h6><FontAwesomeIcon icon={faClock} /> Frequency Control</h6>
                                      <div className="targeting-row">
                                        <span className="targeting-label">Max Frequency:</span>
                                        <span className="targeting-value">
                                          {item.frequency_control.max_frequency} {item.frequency_control.event?.toLowerCase()}s per {item.frequency_control.interval_days} days
                                        </span>
                                      </div>
                                    </div>
                                  )}

                                  {item.excluded_publisher_categories?.length > 0 && (
                                    <div className="targeting-full">
                                      <h6>Excluded Categories</h6>
                                      <div className="targeting-row">
                                        <div className="targeting-tags">
                                          {item.excluded_publisher_categories.map((cat, i) => (
                                            <span key={i} className="targeting-tag excluded">{cat.replace(/_/g, ' ')}</span>
                                          ))}
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </>
                              )}

                              {/* Ad Group specific details (Google Ads new format) */}
                              {itemsType === 'ad_groups' && item.ads?.length > 0 && (
                                <div className="creatives-full">
                                  <h6><FontAwesomeIcon icon={faAd} /> Ads ({item.ads.length})</h6>
                                  {item.ads.map((ad, adIdx) => (
                                    <div key={adIdx} className="ad-details">
                                      <div className="creative-row">
                                        <span className="creative-label">Type:</span>
                                        <span className="creative-value">{ad.ad_type?.replace(/_/g, ' ')}</span>
                                      </div>
                                      {ad.final_url && (
                                        <div className="creative-row">
                                          <span className="creative-label">URL:</span>
                                          <span className="creative-value url">{ad.final_url}</span>
                                        </div>
                                      )}
                                      {ad.headlines?.length > 0 && (
                                        <div className="creative-row">
                                          <span className="creative-label">Headlines ({ad.headlines.length}):</span>
                                          <ul className="messaging-list headlines-list">
                                            {ad.headlines.slice(0, 5).map((headline, i) => (
                                              <li key={i}>{headline}</li>
                                            ))}
                                            {ad.headlines.length > 5 && (
                                              <li className="more-items">+{ad.headlines.length - 5} more</li>
                                            )}
                                          </ul>
                                        </div>
                                      )}
                                      {ad.descriptions?.length > 0 && (
                                        <div className="creative-row messaging-row">
                                          <span className="creative-label">Descriptions ({ad.descriptions.length}):</span>
                                          <ul className="messaging-list">
                                            {ad.descriptions.slice(0, 3).map((desc, i) => (
                                              <li key={i}>{desc}</li>
                                            ))}
                                            {ad.descriptions.length > 3 && (
                                              <li className="more-items">+{ad.descriptions.length - 3} more</li>
                                            )}
                                          </ul>
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              )}

                              {/* Full Creative Details (old format or ad_sets) */}
                              {item.creatives && (
                                <div className="creatives-full">
                                  <h6><FontAwesomeIcon icon={faAd} /> Creatives</h6>

                                  {item.creatives.ad_formats?.length > 0 && (
                                    <div className="creative-row">
                                      <span className="creative-label">Ad Formats:</span>
                                      <span className="creative-value">{item.creatives.ad_formats.join(', ')}</span>
                                    </div>
                                  )}

                                  {item.creatives.messaging?.length > 0 && (
                                    <div className="creative-row messaging-row">
                                      <span className="creative-label">Messaging Options:</span>
                                      <ul className="messaging-list">
                                        {item.creatives.messaging.map((msg, i) => (
                                          <li key={i}>{msg}</li>
                                        ))}
                                      </ul>
                                    </div>
                                  )}

                                  {item.creatives.call_to_action && (
                                    <div className="creative-row">
                                      <span className="creative-label">Call to Action:</span>
                                      <span className="creative-cta-badge">{item.creatives.call_to_action}</span>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          )}

                          {/* Collapsed Summary */}
                          {!isExpanded && (
                            <div className="targeting-summary">
                              {targeting?.geo?.length > 0 && (
                                <span className="summary-tag">
                                  <FontAwesomeIcon icon={faGlobe} /> {targeting.geo.slice(0, 2).join(', ')}
                                  {targeting.geo.length > 2 && ` +${targeting.geo.length - 2}`}
                                </span>
                              )}
                              {targeting?.keywords?.length > 0 && (
                                <span className="summary-tag">
                                  <FontAwesomeIcon icon={faTag} /> {targeting.keywords.length} keywords
                                </span>
                              )}
                              {targeting?.interests?.length > 0 && (
                                <span className="summary-tag">
                                  {targeting.interests.length} interests
                                </span>
                              )}
                              {targeting?.placements?.length > 0 && (
                                <span className="summary-tag">
                                  {targeting.placements.length} placements
                                </span>
                              )}
                              {(item.creatives?.call_to_action) && (
                                <span className="summary-tag cta">
                                  CTA: {item.creatives.call_to_action}
                                </span>
                              )}
                              {itemsType === 'ad_groups' && item.ads?.length > 0 && (
                                <span className="summary-tag">
                                  <FontAwesomeIcon icon={faAd} /> {item.ads.length} ad{item.ads.length !== 1 ? 's' : ''}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
                })}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Strategy Section */}
      {strategy && (
        <div className="plan-section">
          <button
            className="section-header"
            onClick={() => toggleSection('strategy')}
          >
            <div className="section-title">
              <FontAwesomeIcon icon={faCompass} className="section-icon" />
              <h3>Strategy</h3>
            </div>
            <FontAwesomeIcon
              icon={expandedSections.strategy ? faChevronUp : faChevronDown}
            />
          </button>

          {expandedSections.strategy && (
            <div className="section-content strategy-content">
              {strategy.media_mix_rationale && (
                <div className="strategy-block">
                  <h4>Media Mix Rationale</h4>
                  <p>{strategy.media_mix_rationale}</p>
                </div>
              )}

              {strategy.seasonality_considerations && (
                <div className="strategy-block">
                  <h4>Seasonality Considerations</h4>
                  <p>{strategy.seasonality_considerations}</p>
                </div>
              )}

              {strategy.competitive_analysis && (
                <div className="strategy-block">
                  <h4>Competitive Analysis</h4>
                  <p>{strategy.competitive_analysis}</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Timeline Section */}
      {timeline?.phases && timeline.phases.length > 0 && (
        <div className="plan-section">
          <button
            className="section-header"
            onClick={() => toggleSection('timeline')}
          >
            <div className="section-title">
              <FontAwesomeIcon icon={faClock} className="section-icon" />
              <h3>Campaign Timeline</h3>
              <span className="section-badge">{timeline.phases.length} phases</span>
            </div>
            <FontAwesomeIcon
              icon={expandedSections.timeline ? faChevronUp : faChevronDown}
            />
          </button>

          {expandedSections.timeline && (
            <div className="section-content timeline-content">
              <div className="timeline-phases">
                {timeline.phases.map((phase, idx) => (
                  <div key={idx} className="phase-card">
                    <div className="phase-header">
                      <span className="phase-number">{idx + 1}</span>
                      <div className="phase-title-group">
                        <h4 className="phase-name">{phase.name}</h4>
                        <span className="phase-dates">
                          <FontAwesomeIcon icon={faCalendar} />
                          {phase.start_date} - {phase.end_date}
                        </span>
                      </div>
                      <span className="phase-budget">{formatCurrency(phase.budget_allocation)}</span>
                    </div>
                    <p className="phase-focus">{phase.focus}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Optimization Recommendations Section */}
      {optimizationRecommendations && optimizationRecommendations.length > 0 && (
        <div className="plan-section">
          <button
            className="section-header"
            onClick={() => toggleSection('recommendations')}
          >
            <div className="section-title">
              <FontAwesomeIcon icon={faLightbulb} className="section-icon" />
              <h3>Optimization Recommendations</h3>
              <span className="section-badge">{optimizationRecommendations.length} tips</span>
            </div>
            <FontAwesomeIcon
              icon={expandedSections.recommendations ? faChevronUp : faChevronDown}
            />
          </button>

          {expandedSections.recommendations && (
            <div className="section-content recommendations-content">
              <ul className="optimization-list">
                {optimizationRecommendations.map((rec, idx) => (
                  <li key={idx} className="optimization-item">
                    <span className="opt-number">{idx + 1}</span>
                    <span className="opt-text">{rec}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Create Campaign Modal */}
      <CreateCampaignModal
        isOpen={showCreateModal}
        onClose={handleCloseModal}
        channel={selectedChannel}
        mediaPlanName={mediaPlanName}
        insertionOrderFile={insertionOrderFile}
        onSuccess={(data) => {
          console.log('Campaign created successfully:', data);
        }}
      />
    </div>
  );
};

export default MediaPlanDisplay;

